<template>
    <AppLayout>
        <div class="container mx-auto px-4">
            <div class="flex justify-center mt-16">
                <div class="w-full max-w-md">
                    <div class="bg-white rounded-lg shadow-md overflow-hidden">
                        <div
                            class="bg-gray-50 py-3 px-4 border-b border-gray-200"
                        >
                            <h2 class="text-lg font-medium text-gray-900">
                                {{ $t("messages.loginLogin") }}
                            </h2>
                        </div>

                        <div class="p-6">
                            <!-- RED-286: eID sign-in -->
                            <div v-if="eidAvailable" class="mb-6">
                                <p class="text-sm text-gray-700 mb-3">
                                    {{ $t("messages.eid.heading") }}
                                </p>
                                <div class="flex flex-col gap-2">
                                    <a
                                        v-for="method in eidMethods"
                                        :key="method"
                                        :href="eidUrl(method)"
                                        :data-testid="`login-eid-${method}`"
                                        class="w-full text-center bg-white border border-gray-300 rounded-md py-2 px-4 text-sm font-medium text-gray-800 hover:bg-gray-50"
                                    >
                                        {{ $t(`messages.eid.${method}`) }}
                                    </a>
                                </div>
                                <div v-if="passwordAllowed" class="mt-4 text-center text-xs text-gray-500">
                                    {{ $t("messages.eid.or_password") }}
                                </div>
                            </div>

                            <p
                                v-if="error"
                                class="mb-4 text-sm text-red-600"
                                role="alert"
                                data-testid="login-error"
                            >
                                {{ error }}
                            </p>

                            <p v-if="status" class="mb-4 text-sm text-green-700" role="status">
                                {{ status }}
                            </p>

                            <p v-if="!passwordAllowed" class="text-sm text-gray-600">
                                {{ $t("messages.eid.password_disabled") }}
                            </p>

                            <form v-else @submit.prevent="submit">
                                <!-- Email input -->
                                <div class="mb-4">
                                    <label
                                        for="email"
                                        class="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                        {{ $t("messages.loginEmail") }}
                                    </label>
                                    <div>
                                        <input
                                            id="email"
                                            data-testid="login-email"
                                            type="email"
                                            v-model="form.email"
                                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-blue-500 focus:border-blue-500"
                                            required
                                            autocomplete="email"
                                            autofocus
                                        />
                                        <div
                                            v-if="errors.email"
                                            class="mt-1 text-sm text-red-600"
                                            role="alert"
                                        >
                                            <strong>{{ errors.email }}</strong>
                                        </div>
                                    </div>
                                </div>

                                <!-- Password input -->
                                <div class="mb-4">
                                    <label
                                        for="password"
                                        class="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                        {{ $t("messages.loginPassword1") }}
                                    </label>
                                    <div>
                                        <input
                                            id="password"
                                            data-testid="login-password"
                                            type="password"
                                            v-model="form.password"
                                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-blue-500 focus:border-blue-500"
                                            required
                                            autocomplete="current-password"
                                        />
                                        <div
                                            v-if="errors.password"
                                            class="mt-1 text-sm text-red-600"
                                            role="alert"
                                        >
                                            <strong>{{
                                                errors.password
                                            }}</strong>
                                        </div>
                                    </div>
                                </div>

                                <!-- Remember me checkbox -->
                                <div class="mb-4">
                                    <div class="flex items-center">
                                        <input
                                            id="remember"
                                            type="checkbox"
                                            v-model="form.remember"
                                            class="h-4 w-4 text-blue-600 border-gray-300 rounded-sm focus:ring-blue-500"
                                        />
                                        <label
                                            for="remember"
                                            class="ml-2 block text-sm text-gray-700"
                                        >
                                            {{ $t("messages.loginRememberMe") }}
                                        </label>
                                    </div>
                                </div>

                                <!-- Submit button -->
                                <div class="flex items-center justify-between">
                                    <button
                                        type="submit"
                                        data-testid="login-submit"
                                        class="w-full bg-blue-600 py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        :disabled="processing"
                                    >
                                        {{ $t("messages.loginLoginIn") }}
                                    </button>
                                </div>

                                <!-- Password reset link -->
                                <div v-if="canResetPassword" class="mt-4 text-right">
                                    <Link
                                        :href="route('password.request')"
                                        class="text-sm text-blue-600 hover:text-blue-800"
                                    >
                                        {{ $t('messages.loginForgot') }}
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script setup>
import { Link, useForm } from '@inertiajs/vue3'
import { ref } from 'vue'
import AppLayout from '../../Layouts/AppLayout.vue'

defineProps({
  canResetPassword: { type: Boolean, default: false },
  status: { type: String, default: null },
  error: { type: String, default: null },
  // RED-286: eID is offered only when the flag is on AND a client is
  // configured, so a half-configured environment shows no dead buttons.
  eidAvailable: { type: Boolean, default: false },
  eidMethods: { type: Array, default: () => [] },
  passwordAllowed: { type: Boolean, default: true },
})

const form = useForm({
  email: '',
  password: '',
  remember: false,
})

const eidUrl = method => route('auth.eid.redirect', { method })

const processing = ref(false)
const errors = ref({})

const submit = () => {
  processing.value = true
  errors.value = {}

  form.post(route('login'), {
    onFinish: () => {
      processing.value = false
    },
    onError: e => {
      errors.value = e
    },
  })
}

</script>
